// Apollo's mutate result is not always the Network-tab JSON. Read known
// envelopes (data, double-nested data, networkError.result, onError swallow)
// and treat payload success — or a roster id only when success is missing — as
// persist success. Warnings are review flags, not failures.

type SubmitStudentRosterPayload = {
  success?: boolean | null;
  errors?: Array<{ base?: string | null } | null> | null;
  warningsCount?: number | null;
  roster?: { id?: string | null } | null;
};

type GraphQLErrorLike = { message?: string | null } | null | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function asPayload(value: unknown): SubmitStudentRosterPayload | null {
  if (!isRecord(value)) {
    return null;
  }

  if ("submitStudentRoster" in value) {
    const nested = value.submitStudentRoster;
    return isRecord(nested) ? nested : null;
  }

  const looksLikePayload = "success" in value || "warningsCount" in value || "validRecordsCount" in value;
  if (looksLikePayload && !("variables" in value)) {
    return value as SubmitStudentRosterPayload;
  }

  return null;
}

function fromEnvelope(value: unknown): SubmitStudentRosterPayload | null {
  if (!isRecord(value)) {
    return null;
  }

  return asPayload(value) ?? asPayload(value.data) ?? (isRecord(value.data) ? asPayload(value.data.data) : null);
}

export function getSubmitStudentRosterPayload(resultOrError: unknown): SubmitStudentRosterPayload | null {
  const direct = fromEnvelope(resultOrError);
  if (direct) {
    return direct;
  }

  if (!isRecord(resultOrError)) {
    return null;
  }

  const networkError = resultOrError.networkError;
  if (isRecord(networkError)) {
    const fromNetwork = fromEnvelope(networkError.result) ?? fromEnvelope(networkError);
    if (fromNetwork) {
      return fromNetwork;
    }
  }

  if (isRecord(resultOrError.errors) && !Array.isArray(resultOrError.errors)) {
    return getSubmitStudentRosterPayload(resultOrError.errors);
  }

  return null;
}

export function submitStudentRosterSucceeded(resultOrError: unknown): boolean {
  const payload = getSubmitStudentRosterPayload(resultOrError);
  if (!payload) {
    return false;
  }
  if (payload.success === false) {
    return false;
  }
  if (payload.success === true) {
    return true;
  }
  return Boolean(payload.roster?.id);
}

function graphqlErrorList(resultOrError: unknown): GraphQLErrorLike[] {
  if (!isRecord(resultOrError)) {
    return [];
  }

  const lists = [resultOrError.errors, resultOrError.graphQLErrors];
  return lists.flatMap((list) => (Array.isArray(list) ? list : []));
}

function graphqlErrorMessages(resultOrError: unknown): string | null {
  const messages = graphqlErrorList(resultOrError)
    .map((error) => error?.message)
    .filter((message): message is string => Boolean(message?.trim()));

  if (messages.length > 0) {
    return messages.join(", ");
  }

  if (!isRecord(resultOrError)) {
    return null;
  }

  if (isRecord(resultOrError.errors) && !Array.isArray(resultOrError.errors)) {
    return graphqlErrorMessages(resultOrError.errors);
  }

  return null;
}

function objectMessage(resultOrError: unknown): string | null {
  if (!isRecord(resultOrError) || typeof resultOrError.message !== "string" || !resultOrError.message.trim()) {
    return null;
  }
  return resultOrError.message;
}

export function submitStudentRosterErrorMessage(resultOrError: unknown): string {
  const payload = getSubmitStudentRosterPayload(resultOrError);
  const fromPayload = (payload?.errors ?? [])
    .map((error) => error?.base)
    .filter((message): message is string => Boolean(message))
    .join(", ");

  if (fromPayload) {
    return fromPayload;
  }

  const fromGraphQL = graphqlErrorMessages(resultOrError);
  if (fromGraphQL) {
    return fromGraphQL;
  }

  const fromMessage =
    objectMessage(resultOrError) ??
    (isRecord(resultOrError) && isRecord(resultOrError.errors) && !Array.isArray(resultOrError.errors)
      ? objectMessage(resultOrError.errors)
      : null);
  if (fromMessage) {
    return fromMessage;
  }

  return "Failed to submit roster";
}
